import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Fade,
  IconButton,
  InputAdornment,
  Snackbar,
  TextField,
  Typography,
  Alert,
  Select,
  MenuItem,
  Paper,
} from '@mui/material';
import { Email, Lock, Visibility, VisibilityOff, Language } from '@mui/icons-material';
import { useAuth } from '../../context/studentAuthContext';

const BASE_URL = process.env.REACT_APP_BASE_URL;

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [language, setLanguage] = useState('en');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const { setStudent } = useAuth();
  const navigate = useNavigate();

  const translations = {
    en: { login: 'Student Login', subtitle: 'Log in to access your dashboard', forgot: 'Forgot password?', noAccount: "Don't have an account?", create: 'Create one' },
    ha: { login: 'Shiga Dalibi', subtitle: 'Shiga don samun damar dashboard ɗinku', forgot: 'Manta kalmar sirri?', noAccount: 'Ba ku da asusu?', create: 'Ƙirƙiri ɗaya' },
    ig: { login: 'Nweta Akaụntụ', subtitle: 'Banye iji nweta dashboard gị', forgot: 'Chefuo okwuntughe?', noAccount: 'Ị nwere akaụntụ?', create: 'Mepụta otu' },
    yo: { login: 'Wọle Akọọlẹ', subtitle: 'Wọle lati wọle si dashboard rẹ', forgot: 'Gbagbe ọrọigbaniwọle?', noAccount: 'Ko ni akọọlẹ?', create: 'Ṣẹda ọkan' },
  };

  const t = translations[language];

  const handleCloseSnackbar = () => setSnackbar(prev => ({ ...prev, open: false }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const res = await fetch(`${BASE_URL}/api/auth/students/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
        credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Login failed.');
      localStorage.setItem('token', data.token);
      localStorage.setItem('student', JSON.stringify(data.student));
      setStudent(data.student);
      setSnackbar({ open: true, message: 'Login successful!', severity: 'success' });
      setTimeout(() => navigate('/dashboard'), 1500);
    } catch (err) {
      setSnackbar({ open: true, message: err.message, severity: 'error' });
    } finally { setIsSubmitting(false); }
  };

  return (
    <div className='mt-20'>
    <Box sx={{ minHeight: '100vh', py: 4, background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)', display: 'flex', alignItems: 'center' }}>
      <Container maxWidth="lg">
        <Fade in timeout={800}>
          <Paper
            elevation={24}
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              borderRadius: '24px',
              overflow: 'hidden',
              background: 'rgba(255,255,255,0.95)',
              backdropFilter: 'blur(12px)',
              border: '1px solid rgba(255,255,255,0.3)',
              boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
            }}
          >
            {/* Left Image */}
            <Box
              sx={{
                flex: 1.2,
                display: { xs: 'none', md: 'block' },
                position: 'relative',
                minHeight: '400px',
              }}
            >
              <img
                src="af013cb32ec8306a4e3ccec2eb6d4037b8f01b18.jpg"
                alt="Login Illustration"
                style={{ width: '100%', height: '100%', objectFit: 'cover', transform: 'scale(1.05)', transition: 'transform 0.5s' }}
                onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.1)'}
                onMouseLeave={e => e.currentTarget.style.transform = 'scale(1.05)'}
              />
              <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.6) 100%)', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', p: 4 }}>
                <Typography variant="h3" sx={{ color: '#fff', fontWeight: 800, textShadow: '2px 2px 6px rgba(0,0,0,0.5)' }}>Peer2Learn</Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.85)', mt: 1, fontWeight: 500 }}>Join the future of collaborative learning.</Typography>
              </Box>
            </Box>

            {/* Right Form */}
            <Box sx={{ flex: 1, p: { xs: 4, md: 6 }, position: 'relative' }}>
              {/* Language Selector */}
              <Box sx={{ position: 'absolute', top: 20, right: 20 }}>
                <Select
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  size="small"
                  variant="standard"
                  disableUnderline
                  IconComponent={Language}
                  sx={{ bgcolor: 'rgba(0,0,0,0.05)', px: 2, py: 0.5, borderRadius: '20px', fontSize: '0.8rem' }}
                >
                  <MenuItem value="en">English</MenuItem>
                  <MenuItem value="yo">Yoruba</MenuItem>
                  <MenuItem value="ig">Igbo</MenuItem>
                  <MenuItem value="ha">Hausa</MenuItem>
                </Select>
              </Box>

              {/* Header */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h4" sx={{ fontWeight: 800, mb: 1, background: 'linear-gradient(90deg, #3a86ff, #4361ee)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                  {t.login}
                </Typography>
                <Typography variant="body2" color="text.secondary">{t.subtitle}</Typography>
              </Box>

              {/* Form */}
              <Box component="form" onSubmit={handleSubmit}>
                {[
                  { label: 'Email Address', val: email, set: setEmail, icon: <Email />, type: 'email' },
                  { label: 'Password', val: password, set: setPassword, icon: <Lock />, type: showPassword ? 'text' : 'password' }
                ].map((field, idx) => (
                  <TextField
                    key={idx}
                    fullWidth
                    label={field.label}
                    value={field.val}
                    onChange={(e) => field.set(e.target.value)}
                    margin="normal"
                    required
                    type={field.type}
                    InputProps={{
                      startAdornment: <InputAdornment position="start" sx={{ color: '#3a86ff' }}>{field.icon}</InputAdornment>,
                      endAdornment: field.label === 'Password' ? (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowPassword(!showPassword)}>{showPassword ? <VisibilityOff /> : <Visibility />}</IconButton>
                        </InputAdornment>
                      ) : null,
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': { borderRadius: '16px', transition: '0.3s', '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' } },
                    }}
                  />
                ))}

                <Typography component={Link} to="/forgot-password" variant="body2" sx={{ display: 'inline-block', color: '#3a86ff', py: 1, textDecoration: 'none', '&:hover': { color: '#4361ee' } }}>
                  {t.forgot}
                </Typography>

                <Button
                  fullWidth
                  type="submit"
                  variant="contained"
                  disabled={isSubmitting}
                  sx={{
                    py: 2,
                    mt: 2,
                    borderRadius: '16px',
                    fontWeight: 700,
                    textTransform: 'none',
                    fontSize: '1rem',
                    background: 'linear-gradient(90deg, #3a86ff, #4361ee)',
                    boxShadow: '0 10px 20px rgba(58, 134, 255, 0.3)',
                    transition: 'all 0.3s ease',
                    '&:hover': { transform: 'translateY(-3px)', boxShadow: '0 15px 30px rgba(58, 134, 255, 0.4)', background: 'linear-gradient(90deg, #4361ee, #3a86ff)' },
                  }}
                >
                  {isSubmitting ? 'Logging in...' : 'Login'}
                </Button>

                <Typography textAlign="center" sx={{ mt: 3 }}>
                  {t.noAccount}{' '}
                  <Link to="/register" style={{ textDecoration: 'none', color: '#3a86ff', fontWeight: 700 }}>
                    {t.create}
                  </Link>
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Fade>
      </Container>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} variant="filled" sx={{ borderRadius: '12px' }}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
    </div>
  );
}